// JobMatch NG - International & National Skilled Occupations Taxonomy
// Grounded in ILO ISCO-08 (Craft and Related Trades Workers) & Nigerian Artisan Ecosystem

export interface ServiceOccupation {
  id: string;
  name: string;
  normalizedName: string;
  categorySlug: string; // Links to existing 12 categories for 100% backward compatibility
  parentCategoryName: string;
  parentFamily: string;
  description: string;
  aliases: string[];
  skills: string[];
  typicalPriceRange?: string;
  displayOnHomepage?: boolean;
  isActive: boolean;
}

export interface OccupationalFamily {
  id: string;
  name: string;
  description: string;
  iconName: string;
  defaultCategorySlug: string;
}

export const OCCUPATIONAL_FAMILIES: OccupationalFamily[] = [
  {
    id: 'construction-building',
    name: 'Construction & Building',
    description: 'Structural masonry, foundation, bricklaying, roofing, plastering, screeding, tiling, and general building maintenance.',
    iconName: 'Layers',
    defaultCategorySlug: 'bricklayer',
  },
  {
    id: 'plumbing-water',
    name: 'Plumbing & Water Systems',
    description: 'Sanitary plumbing, borehole submersible pumps, drainage, leak detection, water treatment, and pipe fitting.',
    iconName: 'Wrench',
    defaultCategorySlug: 'plumber',
  },
  {
    id: 'electrical-power',
    name: 'Electrical, Power & Renewable Energy',
    description: 'Residential & industrial electrical wiring, solar panel installation, inverters, battery banks, and generators.',
    iconName: 'Zap',
    defaultCategorySlug: 'electrician',
  },
  {
    id: 'hvac-refrigeration',
    name: 'HVAC & Refrigeration',
    description: 'Split inverter air-conditioners, cold rooms, chillers, commercial freezers, ducting, and ventilation systems.',
    iconName: 'Fan',
    defaultCategorySlug: 'ac-technician',
  },
  {
    id: 'metal-fabrication',
    name: 'Metal & Fabrication',
    description: 'Welding, wrought iron gates, security grilles, steel roofing trusses, stainless steel, and structural metalwork.',
    iconName: 'Flame',
    defaultCategorySlug: 'welder',
  },
  {
    id: 'automotive-vehicle',
    name: 'Automotive & Vehicle Diagnostics',
    description: 'Auto mechanical repair, auto electrical, diesel engines, panel beating, spray painting, and wheel alignment.',
    iconName: 'Car',
    defaultCategorySlug: 'other',
  },
  {
    id: 'wood-furniture-interiors',
    name: 'Wood, Furniture & Interiors',
    description: 'Custom joinery, cabinet making, upholstery, bespoke sofas, hardwood doors, and interior woodwork.',
    iconName: 'Armchair',
    defaultCategorySlug: 'furniture-maker',
  },
  {
    id: 'glass-windows',
    name: 'Glass, Windows & Glazing',
    description: 'Aluminium window fabrication, architectural glass installation, shower glass cubicles, and glazier services.',
    iconName: 'Square',
    defaultCategorySlug: 'other',
  },
  {
    id: 'security-low-voltage',
    name: 'Security, Low-Voltage & Smart Systems',
    description: 'CCTV cameras, electric fences, gate automation, intercoms, access control, smart home, and telecom cabling.',
    iconName: 'Shield',
    defaultCategorySlug: 'other',
  },
  {
    id: 'appliance-electronics',
    name: 'Appliance & Electronics Repair',
    description: 'Washing machines, microwave ovens, refrigerators, freezers, gas cookers, TV/audio equipment, and electronics.',
    iconName: 'Tv',
    defaultCategorySlug: 'appliance-repair',
  },
  {
    id: 'machinery-industrial',
    name: 'Machinery & Industrial Systems',
    description: 'Pumps, compressors, hydraulics, pneumatics, milling, industrial plant maintenance, and mechanical fitting.',
    iconName: 'Cog',
    defaultCategorySlug: 'other',
  },
  {
    id: 'textile-fashion-craft',
    name: 'Textile, Leather & Fashion Crafts',
    description: 'Bespoke tailoring, dressmaking, embroidery, shoemaking, cobbling, leatherwork, and garment restoration.',
    iconName: 'Scissors',
    defaultCategorySlug: 'other',
  },
  {
    id: 'craft-handmade-goods',
    name: 'Handicrafts & Artisanal Fabrication',
    description: 'Pottery, wood carving, stone carving, jewellery making, beadwork, musical instrument repair, and decorative arts.',
    iconName: 'Sparkles',
    defaultCategorySlug: 'other',
  },
  {
    id: 'personal-practical-services',
    name: 'Personal Practical Services',
    description: 'Barbering, hair styling, makeup artistry, nail care, and specialist personal grooming.',
    iconName: 'UserCheck',
    defaultCategorySlug: 'other',
  },
];

export const SERVICE_OCCUPATIONS: ServiceOccupation[] = [
  // ==========================================
  // 1. CONSTRUCTION & BUILDING
  // ==========================================
  {
    id: 'occ-bricklayer',
    name: 'Bricklayer & Mason',
    normalizedName: 'Bricklayer & Mason',
    categorySlug: 'bricklayer',
    parentCategoryName: 'Bricklayer',
    parentFamily: 'Construction & Building',
    description: 'Vibrated blocklaying, concrete lintel casting, foundation laying, and structural brickwork.',
    aliases: ['bricklayer', 'mason', 'block layer', 'concrete worker', 'brick mason', 'builder', 'foundation worker'],
    skills: ['block laying', 'foundation casting', 'brickwork', 'concrete mixing', 'lintel casting', 'retaining wall construction'],
    typicalPriceRange: '₦20,000 - ₦250,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-roofer',
    name: 'Roofer & Roofing Technician',
    normalizedName: 'Roofer & Roofing Technician',
    categorySlug: 'carpenter',
    parentCategoryName: 'Carpenter',
    parentFamily: 'Construction & Building',
    description: 'Stone-coated metal roofing sheets, aluminium step-tiles, roof leak repair, timber trussing, and gutters.',
    aliases: ['roofer', 'roofing technician', 'roof installer', 'roof repair specialist', 'gutters installer', 'roof carpentry'],
    skills: ['roof leak repairs', 'stone coated roof tiles', 'aluminium roofing installation', 'timber truss construction', 'flashing and guttering'],
    typicalPriceRange: '₦25,000 - ₦300,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-plasterer',
    name: 'Plasterer & Screeder',
    normalizedName: 'Plasterer & Screeder',
    categorySlug: 'painter',
    parentCategoryName: 'Painter',
    parentFamily: 'Construction & Building',
    description: 'Cement wall plastering, Tyrolean exterior finish, POP screeding, and drywall finishing.',
    aliases: ['plasterer', 'screeder', 'wall screeder', 'tyrolean finisher', 'drywall finisher'],
    skills: ['wall screeding', 'cement plastering', 'tyrolean application', 'crack bridging', 'damp proof plastering'],
    typicalPriceRange: '₦15,000 - ₦150,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-tiler',
    name: 'Tiler & Flooring Specialist',
    normalizedName: 'Tiler & Flooring Specialist',
    categorySlug: 'tiler',
    parentCategoryName: 'Tiler',
    parentFamily: 'Construction & Building',
    description: 'Vitrified porcelain, ceramic tiles, marble and granite countertops, swimming pool tiling, and interlock paving.',
    aliases: ['tiler', 'floor installer', 'marble cutter', 'granite specialist', 'interlocking paver', 'floor tile setter'],
    skills: ['porcelain floor tiling', 'wall tiling', 'granite slab fitting', 'tile waterproofing', 'interlock paving'],
    typicalPriceRange: '₦15,000 - ₦150,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-painter-decorator',
    name: 'Painter & Decorative Finisher',
    normalizedName: 'Painter & Decorative Finisher',
    categorySlug: 'painter',
    parentCategoryName: 'Painter',
    parentFamily: 'Construction & Building',
    description: 'Interior and exterior painting, weather-shield coating, Italian stucco, wallpaper hanging, and damp treatment.',
    aliases: ['painter', 'decorator', 'house painter', 'stucco artist', 'wallpaper installer', 'coatings applicator'],
    skills: ['interior painting', 'weather shield exterior', 'stucco marble paint', 'wallpaper installation', 'anti damp treatment'],
    typicalPriceRange: '₦15,000 - ₦180,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-waterproofing',
    name: 'Waterproofing & Damp Specialist',
    normalizedName: 'Waterproofing & Damp Specialist',
    categorySlug: 'bricklayer',
    parentCategoryName: 'Bricklayer',
    parentFamily: 'Construction & Building',
    description: 'Roof membrane waterproofing, foundation bitumen coating, basement leak sealing, and damp-proof course (DPC).',
    aliases: ['waterproofing specialist', 'damp specialist', 'roof sealant expert', 'dpc installer', 'basement leak repair'],
    skills: ['torch-on bitumen membrane', 'chemical waterproofing', 'foundation dpc sealing', 'concrete injection'],
    typicalPriceRange: '₦30,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 2. PLUMBING & WATER SYSTEMS
  // ==========================================
  {
    id: 'occ-plumber',
    name: 'Sanitary Plumber',
    normalizedName: 'Sanitary Plumber',
    categorySlug: 'plumber',
    parentCategoryName: 'Plumber',
    parentFamily: 'Plumbing & Water Systems',
    description: 'Bathroom fixtures, toilet unblocking, burst pipe repairs, PPR hot/cold piping, and water heaters.',
    aliases: ['plumber', 'sanitary plumber', 'pipe repair specialist', 'bathroom plumber', 'drainage plumber', 'pipe fitter'],
    skills: ['burst pipe repair', 'drain unblocking', 'toilet installation', 'ppr fusion welding', 'water heater servicing'],
    typicalPriceRange: '₦8,000 - ₦50,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-borehole-pump',
    name: 'Borehole & Water Pump Technician',
    normalizedName: 'Borehole & Water Pump Technician',
    categorySlug: 'plumber',
    parentCategoryName: 'Plumber',
    parentFamily: 'Plumbing & Water Systems',
    description: 'Submersible borehole pumps, pressure booster pumps, control boxes, float switches, and borehole drilling servicing.',
    aliases: ['borehole technician', 'water pump repair', 'borehole repair', 'submersible pump technician', 'pump installer', 'borehole driller'],
    skills: ['submersible pump installation', 'borehole recovery', 'pressure booster pump fix', 'float switch wiring', 'pump control box diagnostics'],
    typicalPriceRange: '₦15,000 - ₦120,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-water-treatment',
    name: 'Water Treatment & Filtration Specialist',
    normalizedName: 'Water Treatment & Filtration Specialist',
    categorySlug: 'plumber',
    parentCategoryName: 'Plumber',
    parentFamily: 'Plumbing & Water Systems',
    description: 'Reverse osmosis systems, iron removal filters, sand & carbon filters, UV purifiers, and industrial water plants.',
    aliases: ['water treatment technician', 'water filtration installer', 'ro system technician', 'iron filter specialist'],
    skills: ['iron removal filtration', 'activated carbon bed replacement', 'reverse osmosis installation', 'water testing'],
    typicalPriceRange: '₦20,000 - ₦150,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-drainage-sewage',
    name: 'Drainage & Septic Tank Specialist',
    normalizedName: 'Drainage & Septic Tank Specialist',
    categorySlug: 'plumber',
    parentCategoryName: 'Plumber',
    parentFamily: 'Plumbing & Water Systems',
    description: 'Soakaway pit construction, septic tank unblocking, biodigester installation, and stormwater drainage channels.',
    aliases: ['drainage specialist', 'septic tank specialist', 'soakaway specialist', 'biodigester installer', 'sewage technician'],
    skills: ['soakaway excavation', 'biodigester setup', 'high pressure jetting', 'drainage conduit laying'],
    typicalPriceRange: '₦25,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 3. ELECTRICAL, POWER & RENEWABLE ENERGY
  // ==========================================
  {
    id: 'occ-electrician',
    name: 'Electrical Installation Technician',
    normalizedName: 'Electrical Installation Technician',
    categorySlug: 'electrician',
    parentCategoryName: 'Electrician',
    parentFamily: 'Electrical, Power & Renewable Energy',
    description: 'Conduit wiring, distribution board (DB) overhauls, changeover switches, prepaid meter issues, and lighting.',
    aliases: ['electrician', 'electrical technician', 'wireman', 'building electrician', 'house wiring specialist', 'lighting installer'],
    skills: ['conduit house wiring', 'distribution board balancing', 'changeover switch installation', 'tripping breaker fault finding', 'earthing installation'],
    typicalPriceRange: '₦10,000 - ₦75,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-solar-inverter',
    name: 'Solar Energy & Inverter Technician',
    normalizedName: 'Solar Energy & Inverter Technician',
    categorySlug: 'electrician',
    parentCategoryName: 'Electrician',
    parentFamily: 'Electrical, Power & Renewable Energy',
    description: 'Solar panel roof mounting, hybrid inverter installation, lithium-ion / tubular battery setups, and charge controllers.',
    aliases: ['solar installer', 'solar technician', 'solar panel installer', 'solar inverter technician', 'inverter repair', 'inverter technician', 'battery technician', 'solar guy'],
    skills: ['solar panel installation', 'hybrid inverter configuration', 'lithium battery integration', 'solar charge controller calibration', 'pv system troubleshooting', 'inverter repair'],
    typicalPriceRange: '₦25,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-generator-technician',
    name: 'Generator & Power Plant Technician',
    normalizedName: 'Generator & Power Plant Technician',
    categorySlug: 'generator-technician',
    parentCategoryName: 'Generator Technician',
    parentFamily: 'Electrical, Power & Renewable Energy',
    description: 'Perkins, Mikano, CAT diesel generator servicing, petrol generator overhauls (Firman, Elepaq, Lutian), AVR & ATS panels.',
    aliases: ['generator technician', 'generator repair', 'gen mechanic', 'diesel generator technician', 'mikano technician', 'petrol generator repair', 'ats technician'],
    skills: ['diesel engine overhaul', 'automatic transfer switch (ats)', 'avr calibration', 'alternator rewind', 'petrol generator carburetor fix', 'fuel injection pump servicing'],
    typicalPriceRange: '₦8,000 - ₦120,000',
    displayOnHomepage: true,
    isActive: true,
  },

  // ==========================================
  // 4. HVAC & REFRIGERATION
  // ==========================================
  {
    id: 'occ-ac-technician',
    name: 'Air Conditioning Technician',
    normalizedName: 'Air Conditioning Technician',
    categorySlug: 'ac-technician',
    parentCategoryName: 'AC Technician',
    parentFamily: 'HVAC & Refrigeration',
    description: 'Split inverter AC installation, chemical coil washing, R410A / R22 refrigerant recharging, and compressor diagnostics.',
    aliases: ['ac technician', 'ac repair', 'air conditioner technician', 'hvac technician', 'ac guy', 'air conditioning installer', 'split ac repairer'],
    skills: ['inverter ac diagnostics', 'refrigerant gas top-up', 'chemical coil cleaning', 'compressor replacement', 'copper pipe brazing', 'leak pressure testing'],
    typicalPriceRange: '₦10,000 - ₦65,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-refrigeration-coldroom',
    name: 'Refrigeration & Cold Room Technician',
    normalizedName: 'Refrigeration & Cold Room Technician',
    categorySlug: 'ac-technician',
    parentCategoryName: 'AC Technician',
    parentFamily: 'HVAC & Refrigeration',
    description: 'Commercial walk-in cold rooms, supermarket chillers, ice block machines, industrial condensing units, and blast freezers.',
    aliases: ['cold room technician', 'chiller technician', 'commercial refrigeration technician', 'ice machine repairer', 'industrial cooling specialist'],
    skills: ['cold room panel erection', 'condensing unit overhaul', 'evaporator fan replacement', 'thermostatic expansion valve (txv) tuning', 'low-temperature refrigeration'],
    typicalPriceRange: '₦35,000 - ₦350,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 5. METAL & FABRICATION
  // ==========================================
  {
    id: 'occ-welder',
    name: 'Welder & Structural Steel Fabricator',
    normalizedName: 'Welder & Structural Steel Fabricator',
    categorySlug: 'welder',
    parentCategoryName: 'Welder',
    parentFamily: 'Metal & Fabrication',
    description: 'Security iron gates, burglar-proof window grills, metal staircases, overhead water tank stands, and steel roof trusses.',
    aliases: ['welder', 'fabricator', 'steel fabricator', 'iron worker', 'blacksmith', 'metal fabricator', 'gate maker', 'tank stand fabricator'],
    skills: ['arc welding', 'mig/tig welding', 'security gate fabrication', 'burglar proof grill fabrication', 'water tank stanchion erection', 'structural steel frame assembly'],
    typicalPriceRange: '₦20,000 - ₦200,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-aluminium-fabricator',
    name: 'Aluminium Fabricator & Glazier',
    normalizedName: 'Aluminium Fabricator & Glazier',
    categorySlug: 'welder',
    parentCategoryName: 'Welder',
    parentFamily: 'Metal & Fabrication',
    description: 'Aluminium sliding doors, casement windows, aluminium office partitions, suspended ceilings, and cladding.',
    aliases: ['aluminium fabricator', 'aluminium window installer', 'aluminum worker', 'casement window installer', 'aluminium door specialist'],
    skills: ['aluminium window fabrication', 'casement handle fitting', 'sliding door track alignment', 'aluminium partition erection', 'curtain walling'],
    typicalPriceRange: '₦20,000 - ₦180,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 6. AUTOMOTIVE & VEHICLE
  // ==========================================
  {
    id: 'occ-auto-mechanic',
    name: 'Auto Mechanic & Engine Specialist',
    normalizedName: 'Auto Mechanic & Engine Specialist',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Automotive & Vehicle Diagnostics',
    description: 'Engine mechanical overhaul, brake system servicing, suspension repair, transmission fluid flush, and gearbox fixes.',
    aliases: ['mechanic', 'auto mechanic', 'car mechanic', 'engine specialist', 'motor vehicle repairer', 'brake specialist', 'suspension repairer'],
    skills: ['engine overhaul', 'brake pad replacement', 'shock absorber replacement', 'gearbox diagnostics', 'clutch replacement', 'timing belt replacement'],
    typicalPriceRange: '₦15,000 - ₦150,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-auto-electrician',
    name: 'Auto Electrician & Diagnostics Technician',
    normalizedName: 'Auto Electrician & Diagnostics Technician',
    categorySlug: 'electrician',
    parentCategoryName: 'Electrician',
    parentFamily: 'Automotive & Vehicle Diagnostics',
    description: 'OBD-II computer diagnostics, alternator and starter motor repair, ECU programming, key coding, and car rewiring.',
    aliases: ['auto electrician', 'rewire', 'car rewiring specialist', 'obd scanner technician', 'vehicle diagnostics technician', 'car key programmer'],
    skills: ['obd computer scanning', 'alternator repair', 'starter motor overhaul', 'wiring harness troubleshooting', 'car alarm installation', 'battery load testing'],
    typicalPriceRange: '₦10,000 - ₦80,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-panel-beater',
    name: 'Panel Beater & Auto Spray Painter',
    normalizedName: 'Panel Beater & Auto Spray Painter',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Automotive & Vehicle Diagnostics',
    description: 'Accident chassis straightening, body dent removal, oven-baked spray painting, rust treatment, and scratch buffing.',
    aliases: ['panel beater', 'spray painter', 'auto body repairer', 'dent remover', 'car sprayer', 'oven bake painter'],
    skills: ['chassis realignment', 'dent pulling', 'body filler smoothing', 'oven bake spray painting', 'scratch removal and buffing'],
    typicalPriceRange: '₦25,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-diesel-mechanic',
    name: 'Diesel & Heavy Duty Mechanic',
    normalizedName: 'Diesel & Heavy Duty Mechanic',
    categorySlug: 'generator-technician',
    parentCategoryName: 'Generator Technician',
    parentFamily: 'Automotive & Vehicle Diagnostics',
    description: 'Heavy truck diesel engines (Mack, Sinotruk, DAF, MAN), hydraulic tipper systems, pneumatic brakes, and turbochargers.',
    aliases: ['diesel mechanic', 'heavy duty mechanic', 'truck mechanic', 'tractor mechanic', 'hydraulic tipper repairer'],
    skills: ['diesel injector servicing', 'pneumatic air brake overhaul', 'hydraulic cylinder repair', 'turbocharger replacement'],
    typicalPriceRange: '₦30,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 7. WOOD, FURNITURE & INTERIORS
  // ==========================================
  {
    id: 'occ-carpenter',
    name: 'Carpenter & Joiner',
    normalizedName: 'Carpenter & Joiner',
    categorySlug: 'carpenter',
    parentCategoryName: 'Carpenter',
    parentFamily: 'Wood, Furniture & Interiors',
    description: 'Hardwood door installation, roof woodwork, ceiling framing, wood partitions, and kitchen cabinets.',
    aliases: ['carpenter', 'joiner', 'woodworker', 'wood carpenter', 'door installer', 'frame fitter'],
    skills: ['door hanging', 'lock mortising', 'ceiling nogging', 'wood partition installation', 'structural woodwork'],
    typicalPriceRange: '₦15,000 - ₦120,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-furniture-maker',
    name: 'Furniture Maker & Cabinet Maker',
    normalizedName: 'Furniture Maker & Cabinet Maker',
    categorySlug: 'furniture-maker',
    parentCategoryName: 'Furniture Maker',
    parentFamily: 'Wood, Furniture & Interiors',
    description: 'Custom hardwood dining sets, modular kitchen carcasses, executive office desks, bed frames, and wardrobes.',
    aliases: ['furniture maker', 'cabinet maker', 'wardrobe maker', 'kitchen cabinet maker', 'desk maker', 'bespoke furniture'],
    skills: ['kitchen carcass cabinetry', 'wardrobe installation', 'wood joinery', 'polyurethane wood finishing', 'custom bed frame fabrication'],
    typicalPriceRange: '₦35,000 - ₦450,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-upholsterer',
    name: 'Upholsterer & Sofa Refurbisher',
    normalizedName: 'Upholsterer & Sofa Refurbisher',
    categorySlug: 'furniture-maker',
    parentCategoryName: 'Furniture Maker',
    parentFamily: 'Wood, Furniture & Interiors',
    description: 'Custom royal sofas, sectional couch reupholstery, leather car seat stitching, high-density foam replacement, and dining chairs.',
    aliases: ['upholsterer', 'furniture upholsterer', 'sofa repairer', 'cushion maker', 'leather reupholsterer', 'mattress maker'],
    skills: ['sofa frame webbing', 'high density foam padding', 'leather stitching', 'fabric reupholstery', 'tufting and buttoning'],
    typicalPriceRange: '₦20,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 8. GLASS, WINDOWS & GLAZING
  // ==========================================
  {
    id: 'occ-glazier',
    name: 'Glazier & Glass Specialist',
    normalizedName: 'Glazier & Glass Specialist',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Glass, Windows & Glazing',
    description: 'Tempered frameless shower cubicles, soundproof double-glazed windows, laminated glass railings, mirrors, and glass cutting.',
    aliases: ['glazier', 'glass installer', 'glass cutter', 'shower glass installer', 'glass technician', 'window glazier'],
    skills: ['tempered glass installation', 'frameless shower glass fitting', 'glass beveling and cutting', 'spider glass fitting', 'mirror mounting'],
    typicalPriceRange: '₦25,000 - ₦200,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 9. SECURITY, LOW-VOLTAGE & SMART SYSTEMS
  // ==========================================
  {
    id: 'occ-cctv-security',
    name: 'CCTV & Security Systems Installer',
    normalizedName: 'CCTV & Security Systems Installer',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Security, Low-Voltage & Smart Systems',
    description: 'IP & analog HD CCTV camera installation, NVR/DVR remote mobile monitoring, biometric access control, and intercom systems.',
    aliases: ['cctv installer', 'security camera installer', 'cctv technician', 'intercom installer', 'access control technician', 'security systems technician'],
    skills: ['ip camera configuration', 'nvr network setup', 'cat6 cabling and rj45 crimping', 'video intercom wiring', 'biometric door access setup'],
    typicalPriceRange: '₦20,000 - ₦180,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-electric-fence-automation',
    name: 'Electric Fence & Gate Automation Technician',
    normalizedName: 'Electric Fence & Gate Automation Technician',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Security, Low-Voltage & Smart Systems',
    description: 'High-voltage perimeter electric fences, energizers, automatic sliding & swing gate motors (Centurion), and GSM gate remotes.',
    aliases: ['electric fence installer', 'gate automation technician', 'automatic gate repairer', 'energizer technician', 'electric fence specialist'],
    skills: ['energizer installation', 'high tensile wire tensioning', 'sliding gate motor installation', 'rack and pinion alignment', 'remote transmitter programming'],
    typicalPriceRange: '₦30,000 - ₦220,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-locksmith',
    name: 'Locksmith & Access Security Specialist',
    normalizedName: 'Locksmith & Access Security Specialist',
    categorySlug: 'carpenter',
    parentCategoryName: 'Carpenter',
    parentFamily: 'Security, Low-Voltage & Smart Systems',
    description: 'Smart digital keypad locks, emergency lock opening, master key cutting, safe opening, and high-security deadbolt installation.',
    aliases: ['locksmith', 'lock repairer', 'key cutter', 'smart lock installer', 'safe technician', 'door lock specialist'],
    skills: ['smart digital lock installation', 'emergency lock picking/opening', 'mortise lock replacement', 'key cutting and duplication', 'master key system configuration'],
    typicalPriceRange: '₦10,000 - ₦60,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-telecom-cabling',
    name: 'Network Cabling & Satellite Technician',
    normalizedName: 'Network Cabling & Satellite Technician',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Security, Low-Voltage & Smart Systems',
    description: 'DStv / StarTimes satellite dish alignment, structured Cat6 network cabling, server rack patch panels, and Wi-Fi access points.',
    aliases: ['satellite installer', 'dstv installer', 'network cabling technician', 'cable installer', 'wifi installer', 'dish installer'],
    skills: ['satellite dish alignment', 'lnb replacement', 'cat6 ethernet cabling', 'patch panel termination', 'access point configuration'],
    typicalPriceRange: '₦8,000 - ₦50,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 10. APPLIANCE & ELECTRONICS REPAIR
  // ==========================================
  {
    id: 'occ-appliance-repair',
    name: 'Major Household Appliance Technician',
    normalizedName: 'Major Household Appliance Technician',
    categorySlug: 'appliance-repair',
    parentCategoryName: 'Appliance Repair',
    parentFamily: 'Appliance & Electronics Repair',
    description: 'Front-load & top-load washing machines, microwave magnetrons, dishwashers, electric cookers, and oven thermostats.',
    aliases: ['appliance repair', 'washing machine technician', 'microwave repairer', 'cooker technician', 'dishwasher technician', 'appliance technician'],
    skills: ['washing machine drum/pump repair', 'microwave magnetron testing', 'cooker burner calibration', 'dryer heating element replacement'],
    typicalPriceRange: '₦7,000 - ₦55,000',
    displayOnHomepage: true,
    isActive: true,
  },
  {
    id: 'occ-refrigerator-technician',
    name: 'Refrigerator & Deep Freezer Technician',
    normalizedName: 'Refrigerator & Deep Freezer Technician',
    categorySlug: 'appliance-repair',
    parentCategoryName: 'Appliance Repair',
    parentFamily: 'Appliance & Electronics Repair',
    description: 'Inverter double-door refrigerators, chest deep freezers, thermostat diagnostics, defroster elements, and compressor recharging.',
    aliases: ['refrigerator technician', 'refrigerator repair', 'fridge repair', 'freezer technician', 'deep freezer repairer', 'fridge gas refill'],
    skills: ['refrigerator gas refilling', 'thermostat replacement', 'compressor relay fix', 'condenser leak repair', 'defrost sensor replacement'],
    typicalPriceRange: '₦8,000 - ₦60,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-electronics-repair',
    name: 'Consumer Electronics & Audio-Visual Technician',
    normalizedName: 'Consumer Electronics & Audio-Visual Technician',
    categorySlug: 'appliance-repair',
    parentCategoryName: 'Appliance Repair',
    parentFamily: 'Appliance & Electronics Repair',
    description: 'Smart LED/OLED TV backlight repair, power supply board fixes, home theater amplifiers, and electronic PCB soldering.',
    aliases: ['television repair technician', 'tv repair', 'electronics technician', 'audio equipment technician', 'pcb repairer', 'tv repairer'],
    skills: ['tv led backlight replacement', 'smps power supply troubleshooting', 'component soldering', 'audio amplifier repair', 'motherboard component replacement'],
    typicalPriceRange: '₦8,000 - ₦50,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 11. MACHINERY & INDUSTRIAL
  // ==========================================
  {
    id: 'occ-machinist-fitter',
    name: 'Machinist & Mechanical Fitter',
    normalizedName: 'Machinist & Mechanical Fitter',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Machinery & Industrial Systems',
    description: 'Lathe turning, metal milling, industrial shaft fabrication, gear cutting, and mechanical equipment alignment.',
    aliases: ['machinist', 'fitter', 'mechanical fitter', 'millwright', 'lathe operator', 'machine technician'],
    skills: ['lathe machining', 'shaft fabrication', 'bearing puller extraction and installation', 'mechanical alignment', 'thread cutting'],
    typicalPriceRange: '₦20,000 - ₦150,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-hydraulic-pneumatic',
    name: 'Hydraulic & Pneumatic Systems Technician',
    normalizedName: 'Hydraulic & Pneumatic Systems Technician',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Machinery & Industrial Systems',
    description: 'Hydraulic cylinders, control valves, high-pressure hydraulic hoses crimping, air compressors, and pneumatic actuators.',
    aliases: ['hydraulic technician', 'pneumatic technician', 'hydraulic specialist', 'compressor technician', 'pump technician'],
    skills: ['hydraulic cylinder seal replacement', 'high pressure hose crimping', 'directional valve repair', 'air compressor overhaul'],
    typicalPriceRange: '₦25,000 - ₦200,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 12. TEXTILE, LEATHER & FASHION CRAFTS
  // ==========================================
  {
    id: 'occ-tailor',
    name: 'Bespoke Tailor & Fashion Designer',
    normalizedName: 'Bespoke Tailor & Fashion Designer',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Textile, Leather & Fashion Crafts',
    description: 'Custom senator suits, agbada, formal western suits, wedding gowns, garment alterations, and machine embroidery.',
    aliases: ['tailor', 'dressmaker', 'seamstress', 'fashion designer', 'garment maker', 'cloth repairer', 'embroidery specialist'],
    skills: ['pattern drafting', 'senator suit tailoring', 'embroidery detailing', 'garment alteration and resizing', 'lining installation'],
    typicalPriceRange: '₦10,000 - ₦120,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-shoemaker-leather',
    name: 'Shoemaker & Leather Craftsman',
    normalizedName: 'Shoemaker & Leather Craftsman',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Textile, Leather & Fashion Crafts',
    description: 'Handcrafted leather shoes, sandals, boots, leather bags, shoe sole replacement, cobbling, and leather restoration.',
    aliases: ['shoemaker', 'cobbler', 'leather worker', 'bag maker', 'leather craftsman', 'shoe repairer'],
    skills: ['leather lasting', 'sole stitching and resoling', 'heel rebuild', 'leather bag fabrication', 'leather dyeing and restoration'],
    typicalPriceRange: '₦5,000 - ₦60,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 13. HANDICRAFTS & ARTISANAL FABRICATION
  // ==========================================
  {
    id: 'occ-wood-carver-craft',
    name: 'Wood Carver & Sculptor',
    normalizedName: 'Wood Carver & Sculptor',
    categorySlug: 'furniture-maker',
    parentCategoryName: 'Furniture Maker',
    parentFamily: 'Handicrafts & Artisanal Fabrication',
    description: 'Traditional & contemporary wood carvings, decorative relief panels, wooden masks, architectural corbels, and statues.',
    aliases: ['wood carver', 'sculptor', 'stone carver', 'relief carver', 'decorative wood sculptor'],
    skills: ['chisel carving', 'relief relief sculpting', 'wood seasoning', 'protective waxing and varnishing'],
    typicalPriceRange: '₦20,000 - ₦180,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-potter-ceramic',
    name: 'Potter & Ceramic Artisan',
    normalizedName: 'Potter & Ceramic Artisan',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Handicrafts & Artisanal Fabrication',
    description: 'Hand-thrown pottery, earthenware planters, decorative ceramic vases, kiln firing, and clay glazing.',
    aliases: ['potter', 'ceramic maker', 'clay artisan', 'kiln operator', 'pottery maker'],
    skills: ['potter wheel throwing', 'clay preparation', 'glaze formulating', 'kiln firing'],
    typicalPriceRange: '₦8,000 - ₦75,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-jewellery-maker',
    name: 'Jewellery Maker & Goldsmith',
    normalizedName: 'Jewellery Maker & Goldsmith',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Handicrafts & Artisanal Fabrication',
    description: 'Custom gold and silver smithing, gemstone setting, wedding band resizing, jewellery cleaning, and beadwork.',
    aliases: ['jewellery maker', 'goldsmith', 'silversmith', 'jeweler', 'bead maker', 'ring resizer'],
    skills: ['precious metal soldering', 'stone setting', 'ring resizing', 'polishing and electroplating', 'bead weaving'],
    typicalPriceRange: '₦15,000 - ₦250,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-musical-instrument-repair',
    name: 'Musical Instrument Maker & Repairer',
    normalizedName: 'Musical Instrument Maker & Repairer',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Handicrafts & Artisanal Fabrication',
    description: 'Acoustic and electric guitar setup, drum skin re-heading (talking drum, congas), brass valve servicing, and piano tuning.',
    aliases: ['musical instrument repair', 'guitar luthier', 'piano tuner', 'drum maker', 'luthier', 'instrument technician'],
    skills: ['luthier fret leveling', 'pickup rewiring', 'traditional drum membrane binding', 'acoustic soundboard repair'],
    typicalPriceRange: '₦10,000 - ₦80,000',
    displayOnHomepage: false,
    isActive: true,
  },

  // ==========================================
  // 14. PERSONAL PRACTICAL SERVICES
  // ==========================================
  {
    id: 'occ-barber-hairstylist',
    name: 'Professional Barber & Hair Stylist',
    normalizedName: 'Professional Barber & Hair Stylist',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Personal Practical Services',
    description: 'Men fade haircutting, beard grooming, dreadlocks locking & styling, braiding, and mobile barber services.',
    aliases: ['barber', 'hairstylist', 'hairdresser', 'braider', 'loctician', 'barbering specialist'],
    skills: ['clipper fading and lining', 'beard sculpting', 'dreadlocks retwisting', 'hair braiding and extensions'],
    typicalPriceRange: '₦3,000 - ₦30,000',
    displayOnHomepage: false,
    isActive: true,
  },
  {
    id: 'occ-beauty-makeup',
    name: 'Makeup Artist & Beauty Specialist',
    normalizedName: 'Makeup Artist & Beauty Specialist',
    categorySlug: 'other',
    parentCategoryName: 'Other Skilled Artisans',
    parentFamily: 'Personal Practical Services',
    description: 'Bridal makeup, gele head-tie tying, manicure & pedicure nail technicians, and special occasion beauty grooming.',
    aliases: ['makeup artist', 'nail technician', 'beauty technician', 'gele stylist', 'pedicurist'],
    skills: ['bridal makeup application', 'gele head-tie styling', 'gel nail extensions', 'pedicure sanitization'],
    typicalPriceRange: '₦10,000 - ₦100,000',
    displayOnHomepage: false,
    isActive: true,
  },
];

// Helper to find occupation by name, alias, or skill
export function findMatchingOccupation(query: string): ServiceOccupation | undefined {
  if (!query) return undefined;
  const q = query.toLowerCase().trim();

  // 1. Direct name match
  const byName = SERVICE_OCCUPATIONS.find(
    (o) => o.name.toLowerCase() === q || o.normalizedName.toLowerCase() === q
  );
  if (byName) return byName;

  // 2. Direct alias match
  const byAlias = SERVICE_OCCUPATIONS.find((o) =>
    o.aliases.some((a) => a.toLowerCase() === q || q.includes(a.toLowerCase()) || a.toLowerCase().includes(q))
  );
  if (byAlias) return byAlias;

  // 3. Skill match
  const bySkill = SERVICE_OCCUPATIONS.find((o) =>
    o.skills.some((s) => q.includes(s.toLowerCase()) || s.toLowerCase().includes(q))
  );
  if (bySkill) return bySkill;

  return undefined;
}
